import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ExternalLink, Receipt } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '@/contexts/AuthContext';
import { EmptyState } from '@/components/shared/EmptyState';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { UsageMeter } from '@/components/billing/UsageMeter';
import { billingService, type UsageResponse, type Invoice } from '@/services/billingService';
import { accountService } from '@/services/accountService';
import { formatDate as _formatDateLib } from '@/lib/formatDate';
import { apiErrorMessage } from '@/lib/apiErrorMessage';

type BillingLoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ok'; usage: UsageResponse; invoices: Invoice[] };

function formatCreated(unixSeconds: number): string {
  // Convert unix timestamp to ISO string so formatDate can apply the locale.
  return _formatDateLib(new Date(unixSeconds * 1000).toISOString());
}

function formatAmount(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount / 100);
  } catch {
    return `${amount / 100} ${currency.toUpperCase()}`;
  }
}

interface BillingSectionProps {
  state: BillingLoadState;
  onRetry: () => void;
  onPortal: () => void;
  portalBusy: boolean;
  portalError: string | null;
}

function BillingSection({
  state,
  onRetry,
  onPortal,
  portalBusy,
  portalError,
}: BillingSectionProps) {
  const { t } = useTranslation();

  if (state.status === 'loading') {
    return (
      <div data-testid="billing-skeleton" className="space-y-3">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-2 w-full" />
        <Skeleton className="h-4 w-24" />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div data-testid="billing-error" className="space-y-3">
        <p className="text-sm text-destructive" role="alert">
          {t('billing.error_load')}
        </p>
        <Button variant="outline" size="sm" onClick={onRetry} aria-label={t('billing.retry')}>
          {t('billing.retry')}
        </Button>
      </div>
    );
  }

  const { usage, invoices } = state;

  return (
    <div className="space-y-6 text-sm">
      {/* Plan + usage summary */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{t('billing.plan_label')}</span>
          <span data-testid="billing-tier" className="font-medium">
            {usage.tier
              ? t(`billing.tier.${usage.tier}`, { defaultValue: usage.tier })
              : t('billing.plan_trial')}
          </span>
          <span className="text-muted-foreground">·</span>
          <span className="text-muted-foreground">
            {t(`billing.status.${usage.status}`, { defaultValue: usage.status })}
          </span>
        </div>
        <UsageMeter used={usage.used} cap={usage.cap} mode={usage.mode} variant="full" />
      </div>

      {/* Invoice history */}
      <div className="space-y-3">
        <p className="font-medium">{t('billing.invoices_title')}</p>
        {invoices.length === 0 ? (
          <div data-testid="billing-invoices-empty">
            <EmptyState
              icon={Receipt}
              title={t('billing.invoices_empty_title')}
              subtitle={t('billing.invoices_empty_subtitle')}
            />
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div
              data-testid="billing-invoice-table"
              className="hidden md:block rounded-lg border overflow-hidden"
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('billing.table.number')}</TableHead>
                    <TableHead>{t('billing.table.date')}</TableHead>
                    <TableHead>{t('billing.table.status')}</TableHead>
                    <TableHead className="text-right">{t('billing.table.amount')}</TableHead>
                    <TableHead>{t('billing.table.link')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((inv) => (
                    <TableRow key={inv.id}>
                      <TableCell className="font-mono text-xs">{inv.number ?? '—'}</TableCell>
                      <TableCell>{formatCreated(inv.created)}</TableCell>
                      <TableCell className="capitalize">{inv.status ?? '—'}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatAmount(inv.amount, inv.currency)}
                      </TableCell>
                      <TableCell>
                        {inv.hosted_invoice_url ? (
                          <a
                            href={inv.hosted_invoice_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs hover:underline"
                            aria-label={t('billing.table.view_invoice')}
                          >
                            {t('billing.table.view')}
                            <ExternalLink className="h-3 w-3" aria-hidden="true" />
                          </a>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile cards */}
            <div data-testid="billing-invoice-cards" className="md:hidden space-y-2">
              {invoices.map((inv) => (
                <div key={inv.id} className="rounded-lg border p-3 text-xs space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono">{inv.number ?? '—'}</span>
                    <span className="tabular-nums font-medium">
                      {formatAmount(inv.amount, inv.currency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-muted-foreground">
                    <span>{formatCreated(inv.created)}</span>
                    <span className="capitalize">{inv.status ?? '—'}</span>
                  </div>
                  {inv.hosted_invoice_url && (
                    <a
                      href={inv.hosted_invoice_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 hover:underline"
                      aria-label={t('billing.table.view_invoice')}
                    >
                      {t('billing.table.view')}
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Customer Portal entry */}
      <div className="space-y-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onPortal}
          disabled={portalBusy}
          data-testid="billing-portal-btn"
        >
          {t('billing.manage_payment')}
        </Button>
        {portalError && (
          <div data-testid="billing-portal-error" role="alert" className="space-y-2">
            <p className="text-sm text-destructive">{portalError}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={onPortal}
              disabled={portalBusy}
              data-testid="billing-portal-retry"
              aria-label={t('billing.retry')}
            >
              {t('billing.retry')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Billing state
  const [billingState, setBillingState] = useState<BillingLoadState>({ status: 'loading' });
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  // Privacy / data section state
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const loadBilling = useCallback(() => {
    setBillingState({ status: 'loading' });
    Promise.all([billingService.getUsage(), billingService.listInvoices()])
      .then(([usage, invoices]) => {
        setBillingState({ status: 'ok', usage, invoices });
      })
      .catch(() => {
        setBillingState({ status: 'error' });
      });
  }, []);

  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  async function handlePortal() {
    setPortalBusy(true);
    setPortalError(null);
    try {
      const { url } = await billingService.getCustomerPortalUrl();
      window.location.assign(url);
    } catch (e) {
      // A03: never go mute on failure — surface a retryable inline error.
      setPortalError(apiErrorMessage(e, t));
    } finally {
      setPortalBusy(false);
    }
  }

  if (!user) return null;

  async function handleExport() {
    setExportBusy(true);
    setExportError(null);
    try {
      await accountService.exportData();
    } catch {
      setExportError(t('common.error_generic'));
    } finally {
      setExportBusy(false);
    }
  }

  async function handleDeleteAccount() {
    if (!user) return;
    await accountService.deleteAccount(user.email);
    logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="space-y-8">
      <div className="space-y-1.5">
        <h1 className="text-2xl font-semibold tracking-tight">{t('nav.settings')}</h1>
        <p className="text-sm text-muted-foreground">{t('settings.subtitle')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">{t('settings.account_title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="grid grid-cols-1 gap-y-1 sm:grid-cols-[120px_1fr] sm:gap-y-2">
            <span className="text-muted-foreground">{t('settings.email_label')}</span>
            <span className="min-w-0 break-words">{user.email}</span>
            <span className="mt-2 text-muted-foreground sm:mt-0">{t('settings.name_label')}</span>
            <span className="min-w-0 break-words">{user.name}</span>
            <span className="mt-2 text-muted-foreground sm:mt-0">{t('settings.role_label')}</span>
            <span className="capitalize">{user.role}</span>
          </div>

          <div className="space-y-1.5 pt-2">
            <label htmlFor="ui-language-select" className="text-sm font-medium text-foreground">
              {t('settings.ui_language.label')}
            </label>
            <p className="text-muted-foreground">{t('settings.ui_language.help')}</p>
            <select
              id="ui-language-select"
              data-testid="ui-language-select"
              value={i18n.resolvedLanguage ?? i18n.language}
              onChange={(event) => {
                void i18n.changeLanguage(event.target.value);
              }}
              className="h-9 rounded-lg border bg-background px-3 text-sm"
            >
              <option value="pt-BR">Português (Brasil)</option>
              <option value="pt-PT">Português (Portugal)</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Billing section (F046) */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">{t('billing.section_title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <BillingSection
            state={billingState}
            onRetry={loadBilling}
            onPortal={handlePortal}
            portalBusy={portalBusy}
            portalError={portalError}
          />
        </CardContent>
      </Card>

      {/* Privacy and data section */}
      <div className="space-y-4">
        <div className="space-y-0.5">
          <h2 className="text-base font-semibold">{t('account.privacy.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('account.privacy.subtitle')}</p>
        </div>

        {/* Export card */}
        <Card>
          <CardContent className="space-y-3 pt-6">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <div className="space-y-0.5">
                <p className="text-sm font-medium">{t('account.privacy.export_button')}</p>
                <p className="text-sm text-muted-foreground">{t('account.privacy.export_help')}</p>
              </div>
              <Button
                type="button"
                variant="outline"
                disabled={exportBusy}
                onClick={handleExport}
                className="min-h-[44px] shrink-0"
              >
                {t('account.privacy.export_button')}
              </Button>
            </div>
            {exportError && (
              <p className="text-sm text-destructive" role="alert">
                {exportError}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Danger zone card */}
        <Card data-testid="danger-zone-card" className="border-destructive">
          <CardHeader>
            <CardTitle className="text-base font-medium text-destructive">
              {t('account.delete.danger_title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <p className="text-muted-foreground">{t('account.delete.danger_help')}</p>
            <Button
              type="button"
              variant="destructive"
              className="min-h-[44px]"
              onClick={() => setDeleteDialogOpen(true)}
            >
              {t('account.delete.button')}
            </Button>
          </CardContent>
        </Card>
      </div>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={t('account.delete.confirm_title')}
        description={t('account.delete.confirm_body')}
        confirmLabel={t('account.delete.confirm_cta')}
        cancelLabel={t('account.delete.cancel')}
        variant="destructive"
        requiredPhrase={user?.email}
        phraseLabel={t('account.delete.phrase_label')}
        phrasePlaceholder={t('account.delete.phrase_placeholder')}
        onConfirm={handleDeleteAccount}
      />
    </div>
  );
}
